import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { Badge, Spin, Collapse, Button } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { PIPELINE_COLUMNS } from "./pipeline";
import { WorkflowCard } from "./WorkflowCard";
import { ChatSidebarColumn } from "./ChatSidebarColumn";

function isUnderWarranty(card) {
  if (!card.delivered_at) return false;
  const deliveredDay = dayjs(card.delivered_at);
  return (card.query_parts || []).some(p =>
    (p.effective_warranty_days ?? 0) > 0 &&
    dayjs().diff(deliveredDay, "day") < p.effective_warranty_days
  );
}

export function KanbanBoard({
  grouped,
  urgencySortOn,
  hiddenColumns,
  loading,
  onSelectQuery,
  onDraftClick,
  onSendClick,
  onUrgencyChange,
  onPriceClick,
  chatDrawerPinned, chatDrawerWidth,
  activeChats, setActiveChats,
  chatsLoading, chatsLoadingMore, hasMoreChats,
  chatSearch, setChatSearch, chatSearchFilter, setChatSearchFilter,
  fetchActiveChats, loadMoreChats,
  selectedChat, setSelectedChat,
  queryMatchesByPhone, scrollToSingleQuery, flashingChatIds,
  authState,
  onBulkTemplateOpen,
  sendingCards,
  activeApprovalsMap,
  onPaymentClick,
  onReassignClick,
  onUnparkClick,
  onCancelClick,
}) {
  const boardRef = useRef(null);
  const chatScrollRef = useRef(null);
  const [wfpPanels, setWfpPanels] = useState(["quoted", "unquoted"]);

  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    let isDown = false, dragging = false, startX = 0, startScrollLeft = 0, velX = 0, lastX = 0, animId = null;
    const THRESHOLD = 6;

    const onDown = (e) => {
      if (e.button !== 0) return;
      if (e.target.closest(".ant-btn, .ant-switch, input, .ant-select, .ant-input, .ant-tag")) return;
      isDown = true; dragging = false;
      startX = e.pageX; startScrollLeft = el.scrollLeft; velX = 0; lastX = e.pageX;
      cancelAnimationFrame(animId);
    };

    const onMove = (e) => {
      if (!isDown) return;
      const dx = e.pageX - startX;
      if (!dragging) {
        if (Math.abs(dx) < THRESHOLD) return;
        dragging = true;
        el.style.cursor = "grabbing";
      }
      e.preventDefault();
      velX = e.pageX - lastX;
      lastX = e.pageX;
      el.scrollLeft = startScrollLeft - dx;
    };

    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      el.style.cursor = "grab";
      if (!dragging) return;
      dragging = false;
      document.addEventListener("click", e => { e.stopPropagation(); e.preventDefault(); }, { capture: true, once: true });
      const momentum = () => {
        if (Math.abs(velX) < 0.5) return;
        el.scrollLeft -= velX;
        velX *= 0.88;
        animId = requestAnimationFrame(momentum);
      };
      momentum();
    };

    el.addEventListener("mousedown", onDown);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      el.removeEventListener("mousedown", onDown);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      cancelAnimationFrame(animId);
    };
  }, []);

  const sortCards = (cards) => {
    if (!urgencySortOn) return cards;
    return [...cards].sort((a, b) => (b.urgency || 0) - (a.urgency || 0));
  };

  const handlePhoneClick = (phone) => {
    const digits = phone.replace(/\D/g, "");
    const chat = activeChats.find(c =>
      (c.whatsappId || "").replace(/\D/g, "").endsWith(digits) ||
      (c.phone || "").replace(/\D/g, "").endsWith(digits)
    );
    if (!chat) return;
    setSelectedChat(chat);
    setTimeout(() => {
      const el = document.getElementById(`chat-card-${chat.id}`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
  };

  const chatCol = PIPELINE_COLUMNS.find(c => c.isChat);
  const pipelineCols = PIPELINE_COLUMNS.filter(c => !c.isChat && !hiddenColumns?.has(c.key));

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "row", gap: 12, padding: "12px 16px" }}>

      {/* Pinned chat column */}
      <ChatSidebarColumn
        col={chatCol}
        activeChats={activeChats}
        setActiveChats={setActiveChats}
        chatsLoading={chatsLoading}
        chatsLoadingMore={chatsLoadingMore}
        hasMoreChats={hasMoreChats}
        chatSearch={chatSearch}
        setChatSearch={setChatSearch}
        chatSearchFilter={chatSearchFilter}
        setChatSearchFilter={setChatSearchFilter}
        fetchActiveChats={fetchActiveChats}
        loadMoreChats={loadMoreChats}
        selectedChat={selectedChat}
        setSelectedChat={setSelectedChat}
        queryMatchesByPhone={queryMatchesByPhone}
        scrollToSingleQuery={scrollToSingleQuery}
        flashingChatIds={flashingChatIds}
        authState={authState}
        onBulkTemplateOpen={onBulkTemplateOpen}
        chatScrollRef={chatScrollRef}
      />

      {/* Pipeline board */}
      <div style={{
        flex: 1, minWidth: 0, overflow: "hidden",
        borderRadius: 14,
        border: "1.5px solid #d0d4dc",
        background: "#dde1ea",
        padding: 8,
      }}>
        <div
          ref={boardRef}
          style={{
            display: "flex", flexDirection: "row",
            gap: 12,
            paddingRight: chatDrawerPinned && selectedChat ? chatDrawerWidth : 0,
            overflowX: "auto", overflowY: "hidden",
            height: "100%", alignItems: "flex-start",
            cursor: "grab", WebkitOverflowScrolling: "touch",
            userSelect: "none", WebkitUserSelect: "none",
          }}
        >
          {pipelineCols.map((col, colIdx) => {
            const cards = sortCards(grouped[col.key] || []);
            const isPaid = col.key === "paid";
            const isBeforeNextCol = isPaid && colIdx < pipelineCols.length - 1;

            return (
              <React.Fragment key={col.key}>
              <div
                style={{
                  minWidth: 260, maxWidth: 260,
                  display: "flex", flexDirection: "column",
                  height: "100%",
                  backgroundColor: isPaid ? "#f6ffed" : "#fff",
                  borderRadius: 8,
                  border: isPaid ? "1px solid #b7eb8f" : "1px solid #e8e8e8",
                  borderTop: `4px solid ${col.color}`,
                  overflow: "hidden",
                  flexShrink: 0,
                  position: "relative",
                }}
              >
                {loading && (
                  <div style={{
                    position: "absolute", inset: 0, zIndex: 10,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: "rgba(255,255,255,0.75)", borderRadius: 8,
                  }}>
                    <Spin size="small" />
                  </div>
                )}

                {/* Column header */}
                <div style={{
                  padding: "10px 12px",
                  backgroundColor: isPaid ? "#f6ffed" : "#fff",
                  borderBottom: isPaid ? "1px solid #b7eb8f" : "1px solid #f0f0f0",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  flexShrink: 0,
                }}>
                  <span style={{ fontWeight: 700, fontSize: 12, color: "#262626", display: "flex", alignItems: "center", gap: 6 }}>
                    {col.icon}
                    {col.label}
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {col.key === "waiting_for_price" && (
                      <Button
                        type="text" size="small"
                        style={{ fontSize: 10, color: "#8c8c8c", padding: "0 4px", height: 20 }}
                        onClick={() => setWfpPanels(wfpPanels.length ? [] : ["quoted", "unquoted"])}
                      >
                        {wfpPanels.length ? "Collapse all" : "Expand all"}
                      </Button>
                    )}
                    <Badge count={cards.length} overflowCount={9999} showZero style={{ backgroundColor: "#8c8c8c" }} />
                  </div>
                </div>

                {/* Cards */}
                <div style={{ flex: 1, overflowY: "auto", padding: col.key === "waiting_for_price" ? 0 : 8, display: "flex", flexDirection: "column", gap: col.key === "waiting_for_price" ? 0 : 8 }}>
                  {cards.length === 0 ? (
                    <div style={{ textAlign: "center", color: "#bfbfbf", marginTop: 40, fontSize: 13 }}>No tasks</div>
                  ) : col.key === "waiting_for_price" ? (() => {
                    const quoted   = cards.filter(c => c.parts_with_quotes > 0);
                    const unquoted = cards.filter(c => !c.parts_with_quotes);
                    const renderCards = (list) => (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 8 }}>
                        {list.map(card => (
                          <WorkflowCard
                            key={card.id ?? card.query_pk}
                            card={card}
                            columnColor={col.color}
                            onCardClick={() => onSelectQuery(card)}
                            onDraftClick={onDraftClick}
                            onSendClick={onSendClick}
                            onPhoneClick={handlePhoneClick}
                            onUrgencyChange={onUrgencyChange}
                            onPriceClick={onPriceClick}
                            onPaymentClick={onPaymentClick}
                            onReassignClick={onReassignClick}
                            onUnparkClick={onUnparkClick}
                            onCancelClick={onCancelClick}
                            sendingProgress={sendingCards?.[card.id] ?? null}
                            approvalStatus={activeApprovalsMap?.[card.id ?? card.query_pk] ?? null}
                          />
                        ))}
                      </div>
                    );
                    return (
                      <Collapse
                        activeKey={wfpPanels}
                        onChange={setWfpPanels}
                        ghost
                        expandIcon={({ isActive }) => isActive
                          ? <DownOutlined style={{ fontSize: 10 }} />
                          : <RightOutlined style={{ fontSize: 10 }} />
                        }
                        style={{ width: "100%" }}
                        items={[
                          {
                            key: "quoted",
                            label: (
                              <span style={{ fontSize: 11, fontWeight: 600, color: "#389e0d" }}>
                                Quoted <Badge count={quoted.length} size="small" color="#389e0d" style={{ marginLeft: 4 }} />
                              </span>
                            ),
                            children: quoted.length
                              ? renderCards(quoted)
                              : <div style={{ padding: "4px 8px 12px", fontSize: 12, color: "#bfbfbf" }}>None yet</div>,
                            styles: { body: { padding: 0 } },
                          },
                          {
                            key: "unquoted",
                            label: (
                              <span style={{ fontSize: 11, fontWeight: 600, color: "#8c8c8c" }}>
                                Awaiting <Badge count={unquoted.length} size="small" color="#8c8c8c" style={{ marginLeft: 4 }} />
                              </span>
                            ),
                            children: unquoted.length
                              ? renderCards(unquoted)
                              : <div style={{ padding: "4px 8px 12px", fontSize: 12, color: "#bfbfbf" }}>None</div>,
                            styles: { body: { padding: 0 } },
                          },
                        ]}
                      />
                    );
                  })() : col.key === "completed" ? (() => {
                    const warrantyCards = cards.filter(isUnderWarranty);
                    const closedCards   = cards.filter(c => !isUnderWarranty(c));
                    const renderCards = (list) => list.length === 0
                      ? <div style={{ padding: "4px 8px 12px", fontSize: 12, color: "#bfbfbf" }}>None</div>
                      : list.map(card => (
                          <WorkflowCard
                            key={card.id ?? card.query_pk}
                            card={card}
                            columnColor={col.color}
                            onCardClick={() => onSelectQuery(card)}
                            onDraftClick={onDraftClick}
                            onSendClick={onSendClick}
                            onPhoneClick={handlePhoneClick}
                            onUrgencyChange={onUrgencyChange}
                            onPriceClick={onPriceClick}
                            onPaymentClick={onPaymentClick}
                            onReassignClick={onReassignClick}
                            onUnparkClick={onUnparkClick}
                            onCancelClick={onCancelClick}
                            sendingProgress={sendingCards?.[card.id] ?? null}
                            approvalStatus={activeApprovalsMap?.[card.id ?? card.query_pk] ?? null}
                          />
                        ));
                    return (
                      <Collapse
                        defaultActiveKey={["warranty", "closed"]}
                        ghost
                        expandIcon={({ isActive }) => isActive
                          ? <DownOutlined style={{ fontSize: 10 }} />
                          : <RightOutlined style={{ fontSize: 10 }} />
                        }
                        style={{ width: "100%" }}
                        items={[
                          {
                            key: "warranty",
                            label: (
                              <span style={{ fontSize: 11, fontWeight: 600, color: "#52c41a" }}>
                                Under Warranty <Badge count={warrantyCards.length} size="small" color="#52c41a" style={{ marginLeft: 4 }} />
                              </span>
                            ),
                            children: <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{renderCards(warrantyCards)}</div>,
                            styles: { body: { padding: "4px 8px", background: "#f6ffed", borderRadius: 6 } },
                          },
                          {
                            key: "closed",
                            label: (
                              <span style={{ fontSize: 11, fontWeight: 600, color: "#8c8c8c" }}>
                                Closed <Badge count={closedCards.length} size="small" color="#8c8c8c" style={{ marginLeft: 4 }} />
                              </span>
                            ),
                            children: <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{renderCards(closedCards)}</div>,
                            styles: { body: { padding: "4px 8px", background: "#fafafa", borderRadius: 6 } },
                          },
                        ]}
                      />
                    );
                  })() : (
                    cards.map(card => (
                      <WorkflowCard
                        key={card.id ?? card.query_pk}
                        card={card}
                        columnColor={col.color}
                        onCardClick={() => onSelectQuery(card)}
                        onDraftClick={onDraftClick}
                        onSendClick={onSendClick}
                        onPhoneClick={handlePhoneClick}
                        onUrgencyChange={onUrgencyChange}
                        onPriceClick={onPriceClick}
                        onPaymentClick={onPaymentClick}
                        onReassignClick={onReassignClick}
                        onUnparkClick={onUnparkClick}
                        onCancelClick={onCancelClick}
                        sendingProgress={sendingCards?.[card.id] ?? null}
                        approvalStatus={activeApprovalsMap?.[card.id ?? card.query_pk] ?? null}
                      />
                    ))
                  )}
                </div>
              </div>
              {isBeforeNextCol && (
                <div style={{
                  width: 6, flexShrink: 0, alignSelf: "stretch",
                  background: "#1f1f1f",
                  borderRadius: 4,
                }} />
              )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
